import React, { useEffect, useRef, useState } from 'react'
import { Modal } from 'antd'
import { FiX } from 'react-icons/fi'
import ChatProductResults from './ChatProductResults'
import { chatWithAgent } from '../services/AIAgentServices'
import { useAuth } from '../contexts/authContext/index'
import '../assets/styles/AIChat.css'

const DEFAULT_MESSAGES = [
  { id: 1, role: 'assistant', type: 'text', content: 'Hola! En que puedo ayudarte hoy?' },
]

const MAX_HISTORY_LENGTH = 200
const MAX_INPUT_HEIGHT = 200

const renderInline = (text) => {
  if (!text) {
    return null
  }

  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      const value = part.slice(2, -2)
      return <strong key={`bold-${index}`}>{value}</strong>
    }
    return part
  })
}

const renderMessageContent = (content) => {
  if (!content) {
    return null
  }

  const lines = content.split(/\r?\n/)
  const blocks = []
  let paragraphLines = []
  let listItems = []

  const flushParagraph = () => {
    if (!paragraphLines.length) {
      return
    }
    const text = paragraphLines.join('\n').trim()
    if (text) {
      blocks.push({ type: 'paragraph', text })
    }
    paragraphLines = []
  }

  const flushList = () => {
    if (!listItems.length) {
      return
    }
    blocks.push({ type: 'list', items: listItems })
    listItems = []
  }

  lines.forEach((line) => {
    const trimmed = line.trim()
    if (!trimmed) {
      flushParagraph()
      flushList()
      return
    }

    const listMatch = trimmed.match(/^[-*]\s+(.*)$/)
    if (listMatch) {
      flushParagraph()
      listItems.push(listMatch[1])
      return
    }

    if (listItems.length) {
      flushList()
    }

    paragraphLines.push(line)
  })

  flushParagraph()
  flushList()

  return blocks.map((block, index) => {
    if (block.type === 'list') {
      return (
        <ul key={`list-${index}`} className="bubble-list">
          {block.items.map((item, itemIndex) => (
            <li key={`list-item-${index}-${itemIndex}`}>{renderInline(item)}</li>
          ))}
        </ul>
      )
    }

    return (
      <p key={`paragraph-${index}`} className="bubble-paragraph">
        {renderInline(block.text)}
      </p>
    )
  })
}

const extractProductsFromPayload = (payload) => {
  if (!payload) {
    return []
  }

  const candidates = [
    payload?.reply?.products,
    payload?.products,
    payload?.reply?.data?.products,
  ]

  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length > 0) {
      return candidate
    }
  }

  const rawContent = payload?.reply?.content
  if (typeof rawContent === 'string') {
    const trimmed = rawContent.trim()
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed)
        if (Array.isArray(parsed?.products) && parsed.products.length > 0) {
          return parsed.products
        }
      } catch (_error) {
        /* ignore malformed JSON inside the assistant reply */
      }
    }
  }

  return []
}

const sanitizeHistory = (history) => {
  if (!Array.isArray(history)) {
    return DEFAULT_MESSAGES
  }
  const trimmed = history.slice(-MAX_HISTORY_LENGTH)
  return trimmed.length > 0 ? trimmed : DEFAULT_MESSAGES
}

const AIChat = ({ variant = 'page', onClose, allowClear = false }) => {
  const { currentUser } = useAuth()
  const [messages, setMessages] = useState(DEFAULT_MESSAGES)
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [historyLoaded, setHistoryLoaded] = useState(false)
  const listRef = useRef(null)
  const textareaRef = useRef(null)

  const userId = currentUser?.uid ?? 'anon'
  const storageKey = `ai-chat-history-${userId}`

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight
    }
  }, [messages])

  useEffect(() => {
    let isActive = true
    setHistoryLoaded(false)

    if (typeof window === 'undefined') {
      if (isActive) {
        setMessages(DEFAULT_MESSAGES)
        setHistoryLoaded(true)
      }
      return () => {
        isActive = false
      }
    }

    try {
      const raw = window.localStorage.getItem(storageKey)
      if (raw) {
        const parsed = JSON.parse(raw)
        if (isActive) {
          setMessages(sanitizeHistory(parsed))
          setHistoryLoaded(true)
          return () => {
            isActive = false
          }
        }
      }
    } catch (error) {
      console.warn('No se pudo cargar el historial del asistente', error)
    }

    if (isActive) {
      setMessages(DEFAULT_MESSAGES)
      setHistoryLoaded(true)
    }

    return () => {
      isActive = false
    }
  }, [storageKey])

  useEffect(() => {
    if (!historyLoaded) {
      return
    }
    if (typeof window === 'undefined') {
      return
    }

    try {
      window.localStorage.setItem(storageKey, JSON.stringify(messages))
    } catch (error) {
      console.warn('No se pudo guardar el historial del asistente', error)
    }
  }, [messages, storageKey, historyLoaded])

  useEffect(() => {
    setInput('')
  }, [storageKey])

  useEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) {
      return
    }
    textarea.style.height = 'auto'
    const nextHeight = Math.min(textarea.scrollHeight, MAX_INPUT_HEIGHT)
    textarea.style.height = `${nextHeight}px`
    textarea.style.overflowY = textarea.scrollHeight > MAX_INPUT_HEIGHT ? 'auto' : 'hidden'
  }, [input])

  const sendMessage = async () => {
    const text = input.trim()
    if (!text || sending || !historyLoaded) {
      return
    }

    const userMsg = { id: Date.now(), role: 'user', type: 'text', content: text }
    const baseConversation = [...messages, userMsg]
    const nextConversation =
      baseConversation.length > MAX_HISTORY_LENGTH
        ? baseConversation.slice(-MAX_HISTORY_LENGTH)
        : baseConversation

    setMessages(nextConversation)
    setInput('')
    setSending(true)

    try {
      const convo = nextConversation.map(({ role, content }) => ({ role, content: content || '' }))
      const data = await chatWithAgent(convo)

      const assistantContent = (data?.reply?.content || '').trim()
      const products = extractProductsFromPayload(data)

      setMessages((prev) => {
        const nextMessages = [...prev]
        if (assistantContent) {
          nextMessages.push({
            id: Date.now() + 1,
            role: 'assistant',
            type: 'text',
            content: assistantContent,
          })
        }
        if (products.length > 0) {
          nextMessages.push({
            id: Date.now() + 2,
            role: 'assistant',
            type: 'carousel',
            content: '',
            products,
          })
        }
        if (!assistantContent && products.length === 0) {
          nextMessages.push({
            id: Date.now() + 3,
            role: 'assistant',
            type: 'text',
            content: 'No recibi una respuesta del agente en este momento.',
          })
        }
        return nextMessages.length > MAX_HISTORY_LENGTH
          ? nextMessages.slice(-MAX_HISTORY_LENGTH)
          : nextMessages
      })
    } catch (err) {
      const reply = {
        id: Date.now() + 4,
        role: 'assistant',
        type: 'text',
        content: 'Ocurrio un error consultando el agente: ' + err.message,
      }
      setMessages((prev) => {
        const nextMessages = [...prev, reply]
        return nextMessages.length > MAX_HISTORY_LENGTH
          ? nextMessages.slice(-MAX_HISTORY_LENGTH)
          : nextMessages
      })
    } finally {
      setSending(false)
    }
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    void sendMessage()
  }

  const handleClearChat = () => {
    Modal.confirm({
      title: 'Borrar chat?',
      content: 'Esta accion es permanente y no podras recuperarlo. Deseas continuar?',
      okText: 'Si, borrar',
      cancelText: 'Cancelar',
      okButtonProps: { danger: true },
      onOk: () => {
        const resetMessages = [...DEFAULT_MESSAGES]
        setMessages(resetMessages)
        setInput('')
        if (typeof window !== 'undefined') {
          try {
            window.localStorage.setItem(storageKey, JSON.stringify(resetMessages))
          } catch (error) {
            console.warn('No se pudo limpiar el historial del asistente', error)
          }
        }
      },
    })
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      void sendMessage()
    }
  }

  const hintText = historyLoaded
    ? 'Presiona Enter para enviar - Shift+Enter para nueva línea'
    : 'Recuperando historial...'

  const containerClass =
    variant === 'widget'
      ? 'ai-chat__container ai-chat__container--widget'
      : 'ai-chat__container ai-chat__container--page'

  return (
    <div className={`ai-chat ai-chat--${variant}`}>
      <div className={containerClass}>
        {variant === 'page' && allowClear ? (
          <div className="ai-chat__toolbar">
            <button
              type="button"
              className="ai-chat__clear"
              onClick={handleClearChat}
              disabled={!historyLoaded}
            >
              Borrar chat
            </button>
          </div>
        ) : null}
        {variant === 'widget' ? (
          <div className="ai-chat__header">
            <div className="ai-chat__title">
              <span className="ai-chat__title-main">Asistente inteligente</span>
              <span className="ai-chat__title-sub">Consulta productos y resuelve dudas en segundos</span>
            </div>
            {onClose ? (
              <button
                type="button"
                className="ai-chat__close"
                onClick={onClose}
                aria-label="Cerrar chat de asistente"
              >
                <FiX aria-hidden className="ai-chat__close-icon" />
              </button>
            ) : null}
          </div>
        ) : null}

        <div className="chat-messages" ref={listRef}>
          {messages.map((message) => {
            if (message.type === 'carousel') {
              return (
                <div key={message.id} className={`message ${message.role} message-carousel`}>
                  <div className="carousel-bubble">
                    <ChatProductResults products={message.products || []} />
                  </div>
                </div>
              )
            }

            return (
              <div key={message.id} className={`message ${message.role}`}>
                <div className="bubble">
                  {message.content ? (
                    <div className="bubble-text">{renderMessageContent(message.content)}</div>
                  ) : null}
                </div>
              </div>
            )
          })}
          {sending && (
            <div className="message assistant">
              <div className="bubble bubble-typing">
                <span className="dot" />
                <span className="dot" />
                <span className="dot" />
              </div>
            </div>
          )}
        </div>

        <div className="chat-input">
          <form onSubmit={handleSubmit} className="chat-form">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Escribe tu mensaje..."
              rows={1}
              disabled={!historyLoaded}
            />
            <button type="submit" disabled={!input.trim() || sending || !historyLoaded}>
              Enviar
            </button>
          </form>
          <div className="chat-hint">{hintText}</div>
        </div>
      </div>
    </div>
  )
}

export default AIChat
